import { useEffect, useRef } from 'react';
import { DATA_CHANGED_EVENT } from '../lib/apiCache';
import { isPollingPaused } from '../lib/api';

const FOCUS_MIN_GAP_MS = 5000;

/**
 * Phase 6: keep a screen fresh without a browser reload.
 * Calls refresh({ reason }) after any successful write in the app ('mutation'), when the tab becomes
 * visible / focused again ('focus'), and every intervalMs while the tab is visible ('interval').
 * Timer and focus refreshes are skipped while a receive/email submit has paused polling.
 * refresh should update data silently (no spinner, keep existing data on failure).
 */
export function useLiveRefresh(refresh, { intervalMs = 60000, enabled = true } = {}) {
  const ref = useRef(refresh);
  ref.current = refresh;
  const last = useRef(Date.now());

  useEffect(() => {
    if (!enabled) return undefined;
    let debounce = null;
    const run = (reason) => {
      last.current = Date.now();
      Promise.resolve(ref.current?.({ reason })).catch(() => {});
    };
    const onChanged = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => run('mutation'), 400);
    };
    const onFocus = () => {
      if (document.visibilityState !== 'visible' || isPollingPaused()) return;
      if (Date.now() - last.current >= FOCUS_MIN_GAP_MS) run('focus');
    };
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible' && !isPollingPaused()) run('interval');
    }, intervalMs);
    window.addEventListener(DATA_CHANGED_EVENT, onChanged);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearTimeout(debounce);
      clearInterval(timer);
      window.removeEventListener(DATA_CHANGED_EVENT, onChanged);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [enabled, intervalMs]);
}
